import type { ReactNode } from 'react'
import { Loader2, TriangleAlert, Users } from '@/lib/icons'
import type { BloggerStage, BroadcastPreviewResult } from '@ayub/shared'
import { BLOGGER_STAGE_LABELS, BLOGGER_STAGE_ORDER, formatNumber } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/** Kimga bo'limining uch usuli — `packages/shared/src/api.ts` dagi `BroadcastAudience.kind` bilan bir xil. */
export type AudienceKind = 'all' | 'stage' | 'manual'

export interface AudienceSectionProps {
  kind: AudienceKind
  onKindChange: (kind: AudienceKind) => void
  stage: BloggerStage
  onStageChange: (stage: BloggerStage) => void
  manualCount: number
  onOpenManualPicker: () => void
  preview: BroadcastPreviewResult | undefined
  previewLoading: boolean
  previewError: Error | null
  onRetryPreview: () => void
}

/**
 * "Kimga" bo'limi (`docs/03-app-flow.md` Sahifa 5). Uchta radio — `packages/shared/src/
 * api.ts`dagi izoh: "'barchasi' va 'bosqich bo'yicha' server tomonda so'rov bilan
 * yechiladi, 'qo'lda' esa aniq ro'yxat bo'lgani uchun mijoz tomondan keladi".
 *
 * ⚠️ QAROR: hujjatdagi maket "Barcha faol blogerlar (442)" deb yozadi, lekin server
 * tarafi (`services/broadcast.ts`, `fetchAudienceBloggers()`, `kind: 'all'`) HECH QANDAY
 * bosqich bo'yicha FILTRLAMAYDI — bu haqiqatan HAMMA bloger (shu jumladan Rad
 * etilgan/Bloklangan). Shuning uchun bu yerda ataylab "Barcha blogerlar" deb yozilgan —
 * "faol" so'zini qoldirish admin nazarida "faqat faollarga ketadi" degan noto'g'ri
 * kafolat berardi, aslida server bo'yicha bunday emas.
 *
 * Jonli son (`preview`) faqat HOZIR TANLANGAN usul uchun ko'rsatiladi — uchala variant
 * uchun parallel so'rov yubormaslik uchun (`hooks.ts`dagi `useBroadcastPreview()` bitta
 * `audience`ga bog'langan).
 */
export function AudienceSection({
  kind,
  onKindChange,
  stage,
  onStageChange,
  manualCount,
  onOpenManualPicker,
  preview,
  previewLoading,
  previewError,
  onRetryPreview,
}: AudienceSectionProps) {
  return (
    <fieldset className="flex flex-col gap-3 rounded-card border border-app-border bg-app-card p-4">
      <legend className="px-1 text-sm font-semibold text-app-text">Kimga</legend>

      <div
        role="radiogroup"
        aria-label="Qabul qiluvchilarni tanlash"
        className="flex flex-col gap-2"
      >
        <AudienceOption
          active={kind === 'all'}
          onSelect={() => onKindChange('all')}
          label="Barcha blogerlar"
        />

        <AudienceOption
          active={kind === 'stage'}
          onSelect={() => onKindChange('stage')}
          label="Tanlangan holatdagilar"
        >
          <select
            value={stage}
            onChange={(event) => {
              onStageChange(event.target.value as BloggerStage)
              onKindChange('stage')
            }}
            onClick={(event) => event.stopPropagation()}
            aria-label="Bosqichni tanlash"
            className="h-11 rounded-input border border-app-border bg-app-bg px-2 text-base text-app-text outline-none focus-visible:border-brand"
          >
            {BLOGGER_STAGE_ORDER.map((s) => (
              <option key={s} value={s}>
                {BLOGGER_STAGE_LABELS[s]}
              </option>
            ))}
          </select>
        </AudienceOption>

        <AudienceOption
          active={kind === 'manual'}
          onSelect={() => onKindChange('manual')}
          label="Qo'lda tanlash"
        >
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={(event) => {
              event.stopPropagation()
              onKindChange('manual')
              onOpenManualPicker()
            }}
          >
            <Users size={14} aria-hidden="true" />
            {manualCount > 0 ? `${formatNumber(manualCount)} ta tanlangan` : 'Tanlash...'}
          </Button>
        </AudienceOption>
      </div>

      <AudiencePreviewSummary
        kind={kind}
        manualCount={manualCount}
        preview={preview}
        isLoading={previewLoading}
        error={previewError}
        onRetry={onRetryPreview}
      />
    </fieldset>
  )
}

function AudienceOption({
  active,
  onSelect,
  label,
  children,
}: {
  active: boolean
  onSelect: () => void
  label: string
  children?: ReactNode
}) {
  return (
    <label
      className={cn(
        'flex min-h-11 cursor-pointer flex-wrap items-center gap-3 rounded-button border px-3 py-2 text-sm transition-colors duration-150 ease-out',
        active ? 'border-brand bg-brand-subtle text-brand' : 'border-app-border hover:bg-app-bg',
      )}
    >
      <input
        type="radio"
        name="broadcast-audience"
        checked={active}
        onChange={onSelect}
        className="size-4 shrink-0 accent-brand"
      />
      <span className={cn('font-medium', active ? 'text-brand' : 'text-app-text')}>{label}</span>
      {children}
    </label>
  )
}

function AudiencePreviewSummary({
  kind,
  manualCount,
  preview,
  isLoading,
  error,
  onRetry,
}: {
  kind: AudienceKind
  manualCount: number
  preview: BroadcastPreviewResult | undefined
  isLoading: boolean
  error: Error | null
  onRetry: () => void
}) {
  // "Qo'lda tanlash" hali hech kim belgilanmagan — `manual` bilan bo'sh ro'yxat serverga
  // yuborilsa 400 qaytaradi (`services/broadcast.ts`), shuning uchun bu holatda hech qanday
  // so'rov yuborilmaydi (`hooks.ts` `isAudienceReady()`) — bu yerda ham shunga mos ogohlantirish.
  if (kind === 'manual' && manualCount === 0) {
    return (
      <p className="inline-flex items-center gap-1.5 text-xs font-medium text-app-hint">
        <TriangleAlert size={14} aria-hidden="true" />
        Hali hech kim tanlanmagan.
      </p>
    )
  }

  if (error) {
    return (
      <p className="inline-flex flex-wrap items-center gap-1.5 text-xs font-medium text-urgency-overdue-text">
        {error.message}
        <button type="button" onClick={onRetry} className="shrink-0 underline underline-offset-2">
          Qayta urinish
        </button>
      </p>
    )
  }

  if (isLoading || !preview) {
    return (
      <p className="inline-flex items-center gap-1.5 text-xs text-app-hint">
        <Loader2 size={14} className="animate-spin" aria-hidden="true" />
        Hisoblanmoqda...
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-1 text-xs">
      <p className="text-app-text">
        <span className="tabular font-semibold">{formatNumber(preview.deliverable)} ta</span>{' '}
        blogerga xabar ketadi
        {preview.total !== preview.deliverable && (
          <span className="text-app-hint"> (jami {formatNumber(preview.total)} tadan)</span>
        )}
        .
      </p>
      {preview.botBlocked > 0 && (
        <p className="inline-flex items-center gap-1.5 text-urgency-today-text">
          <TriangleAlert size={13} aria-hidden="true" />
          Shundan <span className="tabular font-medium">
            {formatNumber(preview.botBlocked)} ta
          </span>{' '}
          botni bloklagan — ularga yuborilmaydi.
        </p>
      )}
    </div>
  )
}
